// Headline KPI strip, P&L statement, and alerts — the legacy dashboard's
// top-of-results sections, rendered from the DTO summary.

import { formatUsd } from "../lib/format";
import { useT } from "../lib/i18n";
import type { InfeasiblePalletDTO, SummaryDTO } from "../types";

const OK_SOLVER_STATUSES = new Set(["optimal", "feasible", "evaluate"]);

export function KpiStrip({ summary }: { summary: SummaryDTO }) {
  const t = useT();
  const cost = summary.cost_breakdown;
  const profit = cost.profit ?? 0;
  const total3pl = summary.n_3pl + summary.n_3pl_routed;
  return (
    <div className="kpi-strip">
      <Kpi
        label={t("summary.netProfit")}
        value={formatUsd(profit)}
        sub={t("summary.revMinusCost", {
          rev: formatUsd(cost.revenue ?? 0),
          cost: formatUsd(summary.cost_total_usd),
        })}
        tone={profit >= 0 ? "pos" : "neg"}
      />
      <Kpi
        label={t("summary.pallets")}
        value={String(summary.n_pallets)}
        sub={t("summary.onOwnFleet", { n: summary.n_own_truck })}
      />
      <Kpi
        label={t("summary.threepl")}
        value={String(total3pl)}
        sub={
          total3pl
            ? t("summary.routedSolo", { routed: summary.n_3pl_routed, solo: summary.n_3pl })
            : t("summary.no3pl")
        }
      />
      <Kpi label={t("summary.miles")} value={summary.total_miles.toFixed(0)} sub={t("summary.totalDistance")} />
      <Kpi label={t("summary.hours")} value={summary.total_hours.toFixed(1)} sub={t("summary.driverHourTotal")} />
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: "pos" | "neg";
}) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value${tone ? ` is-${tone}` : ""}`}>{value}</div>
      <div className="kpi-sub">{sub}</div>
    </div>
  );
}

// Per-physical-truck hours over this many → flagged red, matching the
// legacy report (a long day worth the operator's attention).
const OVER_HOURS = 12;

export function FleetSummary({
  summary,
  tripCount,
}: {
  summary: SummaryDTO;
  tripCount: number;
}) {
  const t = useT();
  const trucks = Object.keys(summary.truck_hours).sort();
  const nRoutes = Number(summary.cost_breakdown.n_threepl_routes ?? 0);
  return (
    <div className="summary-card">
      <div className="summary-card-title">{t("summary.fleetSummary")}</div>
      <table className="summary-table">
        <tbody>
          <tr>
            <td>{t("summary.totalPallets")}</td>
            <td>{summary.n_pallets}</td>
          </tr>
          <tr>
            <td>{t("summary.ownTruck")}</td>
            <td>{summary.n_own_truck}</td>
          </tr>
          {summary.n_3pl_routed > 0 && (
            <tr>
              <td>{t("summary.threeplRoutes")}</td>
              <td>{t("summary.threeplRoutesVal", { p: summary.n_3pl_routed, r: nRoutes })}</td>
            </tr>
          )}
          {summary.n_3pl > 0 && (
            <tr>
              <td>{t("summary.threeplSolo")}</td>
              <td>{summary.n_3pl}</td>
            </tr>
          )}
          {summary.n_infeasible > 0 && (
            <tr className="warn-row">
              <td>{t("summary.unassigned")}</td>
              <td>{summary.n_infeasible}</td>
            </tr>
          )}
          <tr>
            <td>{t("summary.ownFleetTrips")}</td>
            <td>{tripCount}</td>
          </tr>
          <tr>
            <td>{t("summary.totalMiles")}</td>
            <td>{summary.total_miles.toFixed(0)} mi</td>
          </tr>
          <tr>
            <td>{t("summary.totalHours")}</td>
            <td>{summary.total_hours.toFixed(1)} h</td>
          </tr>
        </tbody>
      </table>

      {trucks.length > 0 && (
        <>
          <div className="summary-subtitle">{t("summary.perTruck")}</div>
          <table className="summary-table per-truck">
            <thead>
              <tr>
                <th>Truck</th>
                <th>Hours</th>
                <th>Miles</th>
              </tr>
            </thead>
            <tbody>
              {trucks.map((t) => {
                const hrs = summary.truck_hours[t];
                const mi = summary.truck_miles[t] ?? 0;
                return (
                  <tr key={t}>
                    <td>Truck {t}</td>
                    <td className={hrs > OVER_HOURS ? "warn" : undefined}>
                      {hrs.toFixed(1)}h
                    </td>
                    <td>{mi.toFixed(0)} mi</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export function Pnl({ summary }: { summary: SummaryDTO }) {
  const t = useT();
  const c = summary.cost_breakdown;
  const profit = c.profit ?? 0;
  const costRows: [string, number][] = [
    [t("summary.fixedCost"), c.fixed ?? 0],
    [t("summary.driverCost"), c.driver ?? 0],
    [t("summary.fuelCost"), c.fuel ?? 0],
  ];
  if ((c.threepl_routes ?? 0) > 0) costRows.push([t("summary.threeplRoutesCost"), c.threepl_routes]);
  if ((c.threepl_solo ?? 0) > 0) costRows.push([t("summary.threeplSoloCost"), c.threepl_solo]);

  return (
    <div className="summary-card">
      <div className="summary-card-title">{t("summary.pnlTitle")}</div>
      <table className="summary-table pnl-table">
        <tbody>
          <tr className="revenue">
            <td>{t("summary.revenue")}</td>
            <td>{formatUsd(c.revenue ?? 0)}</td>
          </tr>
          {costRows.map(([label, val]) => (
            <tr key={label}>
              <td>{label}</td>
              <td>{formatUsd(val)}</td>
            </tr>
          ))}
          <tr className="subtotal">
            <td>{t("summary.totalCost")}</td>
            <td>{formatUsd(summary.cost_total_usd)}</td>
          </tr>
          <tr className="total">
            <td>{t("summary.netProfit")}</td>
            <td className={profit >= 0 ? "pos" : "neg"}>{formatUsd(profit)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function Alerts({
  summary,
  infeasible,
}: {
  summary: SummaryDTO;
  infeasible: InfeasiblePalletDTO[];
}) {
  const t = useT();
  const badStatus = !OK_SOLVER_STATUSES.has(summary.solver_status);
  const soloN = summary.n_3pl;
  const soloCost = Math.round(summary.cost_breakdown.threepl_solo ?? 0);
  if (!badStatus && infeasible.length === 0 && soloN === 0) return null;
  return (
    <>
      {badStatus && (
        <div className="banner warn">
          {t("summary.alertBadPlan", { status: summary.solver_status })}
        </div>
      )}
      {soloN > 0 && (
        <div className="banner warn">
          {t("summary.alertSolo3pl", { n: soloN, cost: soloCost })}
        </div>
      )}
      {infeasible.length > 0 && (
        <div className="banner warn">
          {t("summary.alertInfeasible", { n: infeasible.length })}
          <ul>
            {infeasible.map((p) => (
              <li key={p.shipment_id}>
                {p.shipment_id}
                {p.dest_zip ? ` → ${p.dest_zip}` : ""}
                {p.reason ? ` (${p.reason})` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
